import type { MessageCitation, CardBlock } from '@/types/chat';
import type { DifyHistoryMessage } from '@/types/chat';
import { normalizeUrl } from '@/lib/urlNormalize';

type DifyCitationLike = {
  id?: string;
  url?: string;
  source?: string;
  link?: string;
  title?: string;
  name?: string;
  filename?: string;
  content?: string;
  snippet?: string;
  text?: string;
  document?: {
    id?: string;
    url?: string;
    title?: string;
    content?: string;
  };
};

function asDifyCitationLike(value: unknown): DifyCitationLike | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as DifyCitationLike;
}

/**
 * Extract citations from Dify history API response.
 * Supports multiple citation formats and deduplicates by URL.
 */
export function extractCitationsFromHistory(
  msg: DifyHistoryMessage,
  messageId: string
): MessageCitation[] {
  const citations: MessageCitation[] = [];

  // Handle retrieval.documents format
  if (msg.retrieval?.documents && Array.isArray(msg.retrieval.documents)) {
    msg.retrieval.documents.forEach((doc, idx: number) => {
      const citation = asDifyCitationLike(doc);
      if (!citation) return;

      const url = citation.url || citation.source || citation.link;
      if (url) {
        citations.push({
          id: citation.id || `cite-${messageId}-${idx}`,
          url: normalizeUrl(url),
          title: citation.title || citation.name || citation.filename || 'Untitled',
          index: idx + 1,
          snippet: citation.content || citation.snippet || citation.text,
        });
      }
    });
  }

  // Handle metadata.citations format
  if (msg.metadata?.citations && Array.isArray(msg.metadata.citations)) {
    msg.metadata.citations.forEach((cite, idx: number) => {
      const citation = asDifyCitationLike(cite);
      if (!citation) return;

      const url = citation.url || citation.source || citation.link;
      if (url) {
        citations.push({
          id: citation.id || `cite-${messageId}-${idx}`,
          url: normalizeUrl(url),
          title: citation.title || citation.name || 'Untitled',
          index: idx + 1,
          snippet: citation.snippet || citation.content || citation.text,
        });
      }
    });
  }

  // Handle direct citations array
  if (Array.isArray(msg.citations)) {
    msg.citations.forEach((cite, idx: number) => {
      const citation = asDifyCitationLike(cite);
      if (!citation) return;

      const url = citation.url || citation.source || citation.link;
      if (url) {
        citations.push({
          id: citation.id || `cite-${messageId}-${idx}`,
          url: normalizeUrl(url),
          title: citation.title || citation.name || 'Untitled',
          index: idx + 1,
          snippet: citation.snippet || citation.content || citation.text,
        });
      }
    });
  }

  // Handle message_retrieval_info format (Dify API format)
  if (msg.message_retrieval_info && Array.isArray(msg.message_retrieval_info)) {
    msg.message_retrieval_info.forEach((retrieval, idx: number) => {
      const citation = asDifyCitationLike(retrieval);
      if (!citation) return;

      const url = citation.url || citation.source || citation.link || citation.document?.url;
      if (url) {
        citations.push({
          id: citation.id || citation.document?.id || `cite-${messageId}-${idx}`,
          url: normalizeUrl(url),
          title: citation.title || citation.document?.title || citation.name || 'Untitled',
          index: idx + 1,
          snippet: citation.snippet || citation.content || citation.document?.content || citation.text,
        });
      }
    });
  }

  // Remove duplicates by normalized URL
  const seenUrls = new Set<string>();
  return citations.filter(citation => {
    if (seenUrls.has(citation.url)) {
      return false;
    }
    seenUrls.add(citation.url);
    return true;
  });
}

/**
 * Merge incoming card block into existing blocks by position.
 * Replaces block at same position if incoming has more cards.
 * Returns sorted array by position.
 */
export function mergeCardBlocksByPosition(
  existing: CardBlock[] | undefined,
  incoming: CardBlock
): CardBlock[] {
  const blocks = Array.isArray(existing) ? [...existing] : [];
  const existingIndex = blocks.findIndex(block => block.position === incoming.position);

  if (existingIndex === -1) {
    blocks.push(incoming);
  } else {
    const current = blocks[existingIndex];
    // Keep the richer version (more cards = more complete during streaming)
    if (incoming.cards.length >= current.cards.length) {
      blocks[existingIndex] = incoming;
    }
  }

  return blocks.sort((a, b) => a.position - b.position);
}

/**
 * Flatten card blocks into single cards array (backward compatibility)
 */
export function flattenCardsFromBlocks(
  cardBlocks: CardBlock[] | undefined
): CardBlock['cards'] | undefined {
  if (!Array.isArray(cardBlocks) || cardBlocks.length === 0) {
    return undefined;
  }

  const flattened = cardBlocks.flatMap(block => block.cards);
  return flattened.length > 0 ? flattened : undefined;
}

/**
 * Deep structural equality check.
 * Handles objects, arrays, primitives. Ignores field order.
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;

  const typeA = typeof a;
  const typeB = typeof b;
  if (typeA !== typeB) return false;

  // Primitives
  if (typeA !== 'object') return a === b;

  // Dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => deepEqual(val, b[idx]));
  }

  if (Array.isArray(a) || Array.isArray(b)) return false;

  // Objects
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key =>
    Object.prototype.hasOwnProperty.call(b, key) &&
    deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
  );
}
